import { type ComponentInstance } from '@uniformdev/canvas';

import {
	getCountryOrStateOptions,
	type FormCountryOrStatePreset,
	UNITED_STATES_COUNTRY_VALUE,
} from './form-country-or-state-options';

export type FormFieldWidth = 'full' | 'half';
export type FormChoiceControlType = 'select' | 'radio' | 'checkboxGroup';
export type FormChoiceColumns = 1 | 2 | 3;
export type FormConsentMode =
	| 'none'
	| 'privacyPolicy'
	| 'generalMarketingOptIn'
	| 'termsOfService'
	| 'eapTermsOfService'
	| 'eapVirtualAgentTermsAndConditions';

type FormFieldBase = {
	id: string;
	type: string;
	width: FormFieldWidth;
};

export type FormNamesFields = FormFieldBase & {
	type: 'formNamesFields';
	required: boolean;
};

export type FormEmailField = FormFieldBase & {
	type: 'formEmailField';
	businessEmailOnly: boolean;
	label?: string;
	requiredMessage?: string;
};

export type FormCompanyField = FormFieldBase & {
	type: 'formCompanyField';
	required: boolean;
	label?: string;
	requiredMessage?: string;
};

export type FormPhoneNumberField = FormFieldBase & {
	type: 'formPhoneNumberField';
	required: boolean;
};

export type FormCustomTextField = FormFieldBase & {
	type: 'formCustomTextField';
	payloadKey: string;
	label: string;
	placeholder?: string;
	helpText?: string;
	required: boolean;
	requiredMessage?: string;
	multiline: boolean;
};

export type FormOption = {
	id: string;
	value: string;
	label: string;
	conditionalFields: FormField[];
};

export type FormCustomChoiceField = FormFieldBase & {
	type: 'formCustomChoiceField';
	controlType: FormChoiceControlType;
	payloadKey: string;
	label: string;
	placeholder?: string;
	helpText?: string;
	required: boolean;
	requiredMessage?: string;
	radioOrCheckboxColumns?: FormChoiceColumns;
	options: FormOption[];
};

export type FormCountryOrStateField = FormFieldBase & {
	type: 'formCountryOrStateField';
	preset: FormCountryOrStatePreset;
	controlType: 'select';
	payloadKey: string;
	label: string;
	required: boolean;
	requiredMessage?: string;
	options: FormOption[];
};

export type FormDatepickerField = FormFieldBase & {
	type: 'formDatepickerField';
	payloadKey: string;
	valueOverride?: string;
	label: string;
	placeholder?: string;
	helpText?: string;
	required: boolean;
	requiredMessage?: string;
};

export type FormChoiceField = FormCustomChoiceField | FormCountryOrStateField;

export type FormField =
	| FormNamesFields
	| FormEmailField
	| FormCompanyField
	| FormPhoneNumberField
	| FormCustomTextField
	| FormDatepickerField
	| FormCustomChoiceField
	| FormCountryOrStateField;

type FlatParameters = Record<string, unknown>;

const asString = (value: unknown): string | undefined =>
	typeof value === 'string' && value.trim() ? value.trim() : undefined;

const asBoolean = (value: unknown): boolean => value === true;

const getParameters = (component: ComponentInstance): FlatParameters =>
	Object.fromEntries(
		Object.entries(component.parameters ?? {}).map(([key, parameter]) => [key, parameter?.value]),
	);

const isContextualEditingPlaceholder = (component: ComponentInstance): boolean =>
	component._id?.startsWith('placeholder_') ?? false;

const getComponents = (component: ComponentInstance, slotName: string): ComponentInstance[] =>
	// prevent hidden placeholder components from showing as duplicate fields
	((component.slots?.[slotName] ?? []) as ComponentInstance[]).filter(
		(childComponent) => !isContextualEditingPlaceholder(childComponent),
	);

const parseOption = (component: ComponentInstance): FormOption => {
	const parameters = getParameters(component);
	return {
		id: component._id ?? '',
		value: asString(parameters['value']) ?? '',
		label: asString(parameters['label']) ?? '',
		conditionalFields: parseFormFields(getComponents(component, 'conditionalFields')),
	};
};

// eslint-disable-next-line complexity -- parses each supported Uniform field definition explicitly.
const parseFormField = (component: ComponentInstance): FormField | null => {
	const parameters = getParameters(component);
	const base = {
		id: component._id ?? '',
		width: parameters['width'] === 'half' ? ('half' as const) : ('full' as const),
	};

	switch (component.type) {
		case 'formNamesFields':
			return { ...base, type: component.type, required: asBoolean(parameters['required']) };
		case 'formEmailField':
			return {
				...base,
				type: component.type,
				businessEmailOnly: asBoolean(parameters['businessEmailOnly']),
				label: asString(parameters['label']),
				requiredMessage: asString(parameters['requiredMessage']),
			};
		case 'formCompanyField':
			return {
				...base,
				type: component.type,
				required: asBoolean(parameters['required']),
				label: asString(parameters['label']),
				requiredMessage: asString(parameters['requiredMessage']),
			};
		case 'formPhoneNumberField':
			return { ...base, type: component.type, required: asBoolean(parameters['required']) };
		case 'formCustomTextField':
			return {
				...base,
				type: component.type,
				payloadKey: asString(parameters['payloadKey']) ?? '',
				label: asString(parameters['label']) ?? '',
				placeholder: asString(parameters['placeholder']),
				helpText: asString(parameters['helpText']),
				required: asBoolean(parameters['required']),
				requiredMessage: asString(parameters['requiredMessage']),
				multiline: asBoolean(parameters['multiline']),
			};
		case 'formDatepickerField': {
			const valueOverride = asString(parameters['valueOverride']);
			return {
				...base,
				type: component.type,
				payloadKey: valueOverride ?? 'date',
				valueOverride,
				label: asString(parameters['label']) ?? 'Date',
				placeholder: asString(parameters['placeholder']),
				helpText: asString(parameters['helpText']),
				required: asBoolean(parameters['required']),
				requiredMessage: asString(parameters['requiredMessage']),
			};
		}
		case 'formCustomChoiceField': {
			const radioOrCheckboxColumns: FormChoiceColumns =
				parameters['radioOrCheckboxColumns'] === 'Three'
					? 3
					: parameters['radioOrCheckboxColumns'] === 'Two'
						? 2
						: 1;
			return {
				...base,
				type: component.type,
				controlType:
					parameters['controlType'] === 'radio' || parameters['controlType'] === 'checkboxGroup'
						? parameters['controlType']
						: 'select',
				payloadKey: asString(parameters['payloadKey']) ?? '',
				label: asString(parameters['label']) ?? '',
				placeholder: asString(parameters['placeholder']),
				helpText: asString(parameters['helpText']),
				required: asBoolean(parameters['required']),
				requiredMessage: asString(parameters['requiredMessage']),
				radioOrCheckboxColumns,
				options: getComponents(component, 'options').map(parseOption),
			};
		}
		case 'formCountryOrStateField': {
			const selectedOption = asString(parameters['options']) ?? asString(parameters['preset']);
			const preset: FormCountryOrStatePreset =
				selectedOption === 'states' || selectedOption === 'usStates'
					? 'states'
					: selectedOption === 'countriesAndStates'
						? 'countriesAndStates'
						: 'countries';
			const isStatesField = preset === 'states';
			let stateField: FormCountryOrStateField | undefined;
			if (preset === 'countriesAndStates') {
				stateField = {
					id: `${base.id}-state`,
					type: 'formCountryOrStateField',
					preset: 'states',
					width: base.width,
					controlType: 'select',
					payloadKey: 'state',
					label: 'State',
					required: asBoolean(parameters['required']),
					options: getCountryOrStateOptions('states').map((option) => ({
						id: option.value,
						...option,
						conditionalFields: [],
					})),
				};
			}
			return {
				...base,
				type: component.type,
				preset,
				controlType: 'select',
				payloadKey: isStatesField ? 'state' : 'country',
				label: asString(parameters['label']) ?? (isStatesField ? 'State' : 'Country'),
				required: asBoolean(parameters['required']),
				requiredMessage: asString(parameters['requiredMessage']),
				options: getCountryOrStateOptions(isStatesField ? 'states' : 'countries').map((option) => ({
					id: option.value,
					...option,
					conditionalFields:
						option.value === UNITED_STATES_COUNTRY_VALUE && stateField ? [stateField] : [],
				})),
			};
		}
		default:
			return null;
	}
};

export const parseFormFields = (components: ComponentInstance[]): FormField[] =>
	components.map(parseFormField).filter((field): field is FormField => field !== null);

export const getFormFields = (component: ComponentInstance): FormField[] =>
	parseFormFields(getComponents(component, 'fields'));

export const getFieldPayloadKeys = (field: FormField): string[] => {
	switch (field.type) {
		case 'formNamesFields':
			return ['firstName', 'lastName'];
		case 'formEmailField':
			return ['email'];
		case 'formCompanyField':
			return ['company'];
		case 'formPhoneNumberField':
			return ['number'];
		case 'formCustomTextField':
		case 'formDatepickerField':
		case 'formCustomChoiceField':
		case 'formCountryOrStateField':
			return field.payloadKey ? [field.payloadKey] : [];
	}
};
